<template>
  <div class="profile">
    <div v-if="accounts.length" class="navbar-item">
      <b-dropdown aria-role="list" position="is-bottom-left" ref="dropdown" :scrollable="true" :max-height="400">
        <template #trigger>
          <b-button class="button" type="is-primary" icon-right="caret-down">
            <div v-if="profile">
              <profile-pic :user="profile" :mini-view="true" :no-click="true" />
              <span>@{{ profile.screen_name }}</span>
            </div>
            <div v-else>
              <div class="media">
                <b-icon class="media-left" type="is-light" icon="user-secret"></b-icon>
                <div class="media-content">
                  Anonymous mode
                </div>
              </div>
            </div>
          </b-button>
        </template>

        <b-dropdown-item v-for="(account, index) in accounts"
                         :key="account.screen_name"
                         :class="{'is-active': profile && index === activeAccountIndex }"
                         @click="switchProfile(index)">
          <div class="media">
            <profile-pic :user="account" :mini-view="true" class="media-left icon" :no-click="true" />
            <div class="media-content">
              {{ account.name }} (@{{ account.screen_name }})
            </div>
          </div>
        </b-dropdown-item>

        <b-dropdown-item :class="{'is-active': profile === null || activeAccountIndex === null}"
                         @click="$store.commit('setActiveAccountIndex', null)" type="is-dark">
          <div class="media">
            <b-icon class="media-left" type="is-dark-grey" icon="user-secret"></b-icon>
            <div class="media-content">
              Anonymous mode
            </div>
            <b-tooltip label="Compose without being logged into an account to prevent any accidental tweets." position="is-left" size="is-small" multilined>
              <b-icon class="media-right" type="is-dark-grey" icon="info-circle"></b-icon>
            </b-tooltip>

          </div>
        </b-dropdown-item>
        <hr class="dropdown-divider"/>

        <b-dropdown-item @click="showNewAccountModal = true">
          <div class="media">
            <b-icon class="media-left" type="is-primary" icon="plus"></b-icon>
            <div class="media-content">
              <h3>Add account&hellip;</h3>
            </div>
          </div>
        </b-dropdown-item>

        <b-dropdown-item v-if="profile" @click="confirmLogOut">
          <div class="media">
            <b-icon class="media-left" type="is-primary" icon="sign-out-alt"></b-icon>
            <div class="media-content">
              <h3>Log out of @{{ profile.screen_name }}&hellip;</h3>
            </div>
          </div>
        </b-dropdown-item>

        <hr v-if="profile" class="dropdown-divider"/>
        <b-dropdown-item v-if="profile" has-link>
          <router-link to="/app/timelines/you" active-class="">
            <div class="media">
              <b-icon class="media-left" type="is-primary" icon="user-circle"></b-icon>
              <div class="media-content">
                <h3>@{{ profile.screen_name }} profile</h3>
              </div>
            </div>
          </router-link>
        </b-dropdown-item>


      </b-dropdown>
    </div>

    <div v-else class="navbar-item">
      <div class="buttons">
        <button @click="login" class="button">
          <b-icon size="is-small" type="is-info" pack="fab" icon="twitter"></b-icon>
          <span>Sign in to Twitter</span>
        </button>
      </div>
    </div>

    <b-modal
        v-model="showNewAccountModal"
        has-modal-card trap-focus scroll="keep"
        aria-role="dialog" class="dialog"
        aria-modal>
      <div class="modal-card">
        <section class="modal-card-body is-titleless has-text-dark">
          <div class="content">
            <p>
              To connect a separate Twitter account, you must first make sure
              <strong class="is-primary">you are logged in to that account</strong> on Twitter.com.
            </p>
            <p>
              To do this, <a href="https://twitter.com/account/switch" target="_blank">Go to account switch on
              Twitter.com</a>
              and switch to the account you wish to use on Thread. You can also
              <a href="https://twitter.com/account/add" target="_blank">add an existing account</a>.
            </p>
            <p>
              Once that's done, come back here and click the button below to authenticate!
            </p>
          </div>

        </section>
        <footer class="modal-card-foot">
          <button class="button" type="button" @click="showNewAccountModal = false">Close</button>
          <button class="button is-primary" @click="login">Add new account</button>
        </footer>
      </div>

    </b-modal>
  </div>
</template>

<script>

import auth from "../api/auth"
import ProfilePic from "./ProfilePic"
import {ToastProgrammatic as Toast} from 'buefy'
import tweets from "../api/tweets";
import store from "../store";

export default {
  name: "AccountLogin",
  components: {ProfilePic},
  computed: {
    profile() {
      return this.$store.getters.isLoggedIn && this.$store.state.auth.profile
    },
    triggerSignIn() {
      return this.$store.state.app.triggers.signIn
    },
    triggerSelectAccount() {
      return this.$store.state.app.triggers.selectAccount
    },
    accounts() {
      return this.$store.state.auth.accounts
    },
    activeAccountIndex: {
      get() {
        return this.$store.state.auth.activeAccountIndex
      }
    }
  },
  data() {
    return {
      loading: false,
      selectedAccount: null,
      showNewAccountModal: false
    }
  },
  watch: {
    // watch for an external trigger to start a new login process
    triggerSignIn(val) {
      if (val) {
        this.$store.commit('clearTrigger', 'signIn')
        if (!this.loading) {
          this.login()
        }
      }
    },
    triggerSelectAccount(val) {
      if (val) {
        this.$store.commit('clearTrigger', 'selectAccount')
        this.$refs.dropdown.toggle()
      }
    },
    profile(val) {
      if (!val) {
        if (this.$route.path !== '/app/compose') {
          this.$router.push('/app/compose')
        }
      }
    }
  },


  mounted() {
    // fired when a new local-storage setting is discovered
    this.$ls.on('new-auth-token', this.loadProfile)

    // if we have an active account, load the profile on mount with switching - this handles refreshing etc
    if (this.activeAccountIndex !== null && this.profile === null) {
      this.switchProfile(this.activeAccountIndex)
    }
  },

  methods: {
    switchProfile(index) {
      store.dispatch('startLoading')
      auth.getAuthHeader(index).then(() => {
        let jwt = this.accounts[index].jwt
        auth.getProfile(jwt).then(response => {
          this.setProfile(jwt, response.data)
          store.dispatch('stopLoading')
        }).catch(() => {
          // if we get an error setting, then just can it
          store.dispatch('stopLoading')
          Toast.open(`Error getting profile of @${this.accounts[index].screen_name}. Please try logging in again.`)
          this.$store.commit('deleteAccount', index)
        })
      }).catch((e) => {
        Toast.open(`Error trying to authenticate with @${this.accounts[index].screen_name}: ${e}. Please try logging in again.`)
        this.$store.commit('deleteAccount', index)
        store.dispatch('stopLoading')
      })
    },

    loadProfile(jwt) {
      if (jwt) {
        auth.getProfile(jwt).then(response => {
          this.setProfile(jwt, response.data)
        })
      }
    },

    setProfile(jwt, profile) {
      this.$store.dispatch('storeAccount', {
        jwt: jwt,
        name: profile.name,
        screen_name: profile.screen_name,
        image_url: profile.image_url
      })
      this.$store.commit('setProfile', profile)
      this.loadFriends()
    },

    login() {
      // open the window early doors so that it doesn't get caught in popup blockers
      let popup = window.open('/authorizing', 'authpopup', 'menubar=0,resizeable=1,width=400,height=600,toolbar=no,location=no,status=no')
      this.showNewAccountModal = false
      this.loading = true
      auth.apiCall('auth/twitter/get-url').then(response => {
        this.$ls.set('pre-auth-token', response.data.token)
        popup.location = response.data.url
        this.checkPopupClosure(popup)
      })
    },
    confirmLogOut() {
      this.$buefy.dialog.confirm({
        message: "<strong>Are you sure you want to log out?</strong> You won't lose any of your drafts, and can log back in at any time.",
        type: 'is-danger',
        hasIcon: true,
        confirmText: 'Yes, log me out!',
        icon: 'twitter',
        iconPack: 'fab',
        scroll: 'keep',
        onConfirm: () => this.$store.commit('deleteAccount', this.$store.state.auth.activeAccountIndex)
      })
    },
    checkPopupClosure(popup) {
      let wait_to_exist = 0;
      let poll_time = 100;
      let pollTimer = window.setInterval(() => {
        if (!popup) {
          // browser may have blocked the popup?
          wait_to_exist += poll_time
          if (wait_to_exist > 1000) {
            window.clearInterval(pollTimer)
            this.loading = true
          }
        } else if (popup.closed) {
          window.clearInterval(pollTimer);
          this.loading = false
        }
      }, poll_time);
    },

		loadFriends() {
			this.$store.commit('clearFriends')
			tweets.getFriends()
				.then(response => {
					this.$store.commit('setFriends', response.data)
				})
				.catch(error => {

				})
    }
  }
}
</script>

